import { Component, h, Element, Prop } from '@stencil/core';
import axios from '../../../utils/get-axios';
import { TealiumProductClickEvent } from '../../../utils/tealium/TealiumProductClickEvent';

@Component({
  tag: 'lilu-product-grid',
  styleUrl: 'lilu-product-grid.scss',
  shadow: true,
})
export class LiluProductGrid {
  @Prop() teasersUrl: string = null;
  @Prop() columns: string = '';
  @Element() el;

  teasersJSON = null;
  addedTeasersPos = 0;
  page = 1;

  async loadTeasers() {
    if (this.teasersUrl) {
      axios
        .get(`${this.teasersUrl}`)
        .then(teasers => {
          this.teasersJSON = teasers.data;
        })
        .catch(error => {
          console.error(error);
        });
    } else {
      const teasersStatic = document.getElementById('ProductListTeaserSlot');
      if (teasersStatic && teasersStatic.innerHTML.trim().length > 0) {
        this.teasersJSON = JSON.parse(teasersStatic.innerHTML);
      }
    }

    this.addTeasers();
  }

  isValidPage(pageInfo = ''): boolean {
    if (pageInfo === 'all') return true;
    if (pageInfo === 'odd' && this.page % 2 !== 0) return true;
    if (pageInfo === 'even' && this.page % 2 === 0) return true;
    if (pageInfo === '' && this.page === 1) return true;
    if (typeof pageInfo === 'number') {
      return pageInfo === this.page;
    } else {
      return pageInfo === this.page.toString();
    }
  }

  updateAddedTeasersPos(teaserInfo) {
    if (teaserInfo.size === 'big') {
      this.addedTeasersPos += 2;
    } else {
      this.addedTeasersPos++;
    }
  }

  getTeaser(teaserInfo, classes = ''): string {
    return `<lilu-product-list-teaser
                linktext="${teaserInfo.teaser.linkText}"
                linkurl="${teaserInfo.teaser.linkUrl}"
                heading="${teaserInfo.teaser.heading}"
                subtitle="${teaserInfo.teaser.subTitle}"
                textcolor="${teaserInfo.teaser.textColor}"
                class="${teaserInfo.size} ${classes} ">
                  <img loading="lazy" slot="image" class="img" src="${teaserInfo.teaser.src}" alt="${teaserInfo.teaser.alt}"/>
              </lilu-product-list-teaser>`;
  }

  addDefaultTeasers() {
    const tiles = this.getAllTiles();
    const lastTile = tiles[tiles.length - 1];

    this.insertTeaserHTML(lastTile, this.getTeaser(this.teasersJSON.defaultSmall, 'teaser-hidden teaser-default-small'));

    const totalTilesWithTeasers = tiles.length + this.addedTeasersPos;
    const slotsUsedInLastRowDesktop = totalTilesWithTeasers % 3;
    const slotsUsedInLastRowMobile = totalTilesWithTeasers % 2;

    if (slotsUsedInLastRowDesktop === 1) {
      this.insertTeaserHTML(lastTile, this.getTeaser(this.teasersJSON.defaultBig, 'teaser-hidden teaser-default-big teaser-show-desktop'));
    } else if (slotsUsedInLastRowDesktop === 2) {
      this.insertTeaserHTML(lastTile, this.getTeaser(this.teasersJSON.defaultSmall, 'teaser-hidden teaser-default-small teaser-show-desktop'));
    }

    if (slotsUsedInLastRowMobile === 1) {
      this.insertTeaserHTML(lastTile, this.getTeaser(this.teasersJSON.defaultSmall, 'teaser-hidden teaser-default-small teaser-show-mobile'));
    }
  }

  insertPos0TeaserHTML() {
    this.teasersJSON.teasers?.forEach(teaserInfo => {
      if (teaserInfo.afterPosition === 0 && this.isValidPage(teaserInfo.page) && this.el.querySelector('lilu-product-tile')) {
        this.el.querySelector('lilu-product-tile').insertAdjacentHTML('beforebegin', this.getTeaser(teaserInfo));
        this.updateAddedTeasersPos(teaserInfo);
      }
    });
  }

  setTeaserByPositionAndPage(tile, pos: number) {
    this.teasersJSON.teasers?.forEach(teaserInfo => {
      if (pos === teaserInfo.afterPosition && this.isValidPage(teaserInfo.page)) {
        this.insertTeaserHTML(tile, this.getTeaser(teaserInfo));
        this.updateAddedTeasersPos(teaserInfo);
      }
    });
  }

  insertTeaserHTML(tile: HTMLElement, teaser: string) {
    tile?.insertAdjacentHTML('afterend', teaser);
  }

  getRealPosition(pos: number) {
    return pos + this.addedTeasersPos + 1;
  }

  getAllTiles(): HTMLElement[] {
    return this.el.querySelectorAll('lilu-product-tile');
  }

  getAllClickableDivsForTracking(): HTMLElement[] {
    const productTiles: HTMLElement[] = this.getAllTiles();
    const clickableDivsForTracking: HTMLElement[] = [];

    productTiles.forEach(tile => {
      const productId: string = tile.getAttribute('data-product-id');
      const clickableDivs = tile.shadowRoot.querySelectorAll('.img, .bottom');
      clickableDivs.forEach((clickableDiv: HTMLElement) => {
        clickableDiv.setAttribute('tealium-data-product-id', productId);
        clickableDivsForTracking.push(clickableDiv);
      });
    });

    return clickableDivsForTracking;
  }

  addTeasers() {
    if (this.teasersJSON) {
      this.insertPos0TeaserHTML();
      this.getAllTiles().forEach((tile, index) => {
        this.setTeaserByPositionAndPage(tile, index + 1);
      });
      this.addDefaultTeasers();
    }
    this.el.shadowRoot.querySelector('.grid').classList.add('show');
  }

  setPageNumberFromURL() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const pageInURL = urlParams.get('page');

    if (pageInURL) {
      try {
        this.page = parseInt(pageInURL);
      } catch {
        this.page = 1;
      }
    }
  }

  componentWillLoad() {
    this.setPageNumberFromURL();
  }

  componentDidLoad() {
    this.loadTeasers();
    const tealiumProductClickEvent = new TealiumProductClickEvent();
    tealiumProductClickEvent.addClickEvent(this.getAllClickableDivsForTracking(), 'tealium-data-product-id');
  }

  render() {
    return (
      <section class="product-grid">
        <slot name="link"></slot>
        <slot name="hr"></slot>
        <div class={`grid ${this.columns}`}>
          <slot></slot>
        </div>
      </section>
    );
  }
}
